// lib/trends.js
// 회원권 추세 화면(/trends)의 API와 화면 표시 규칙
// (docs/architecture/admin-trends-design.md 2·4절). 화면 컴포넌트는 응답 모양을 이 모듈로만 본다
import { apiJson } from "./api";
import {
  CONTACT_STATUSES,
  contactResultLabel,
  membershipCountText,
  noteDate,
  noteDateTime,
  shortDate,
} from "./memberNotes";

// ---------------- 탭·기간·이유·연락 상태 (URL 쿼리 ?tab=&months=&reason=&contact=) ----------------

// 탭 순서는 사장님과 정한 그대로다
export const TABS = [
  { key: "overview", label: "추세" },
  { key: "at-risk", label: "이탈 위험" },
  { key: "trials", label: "체험 전환" },
  { key: "operations", label: "운영" },
];
// 처음 여는 탭은 이탈 위험: 데스크에서 매일 보는 "오늘 연락할 회원"이 먼저 보인다(설계 검토 C13)
export const DEFAULT_TAB = "at-risk";

export const MONTH_OPTIONS = [6, 12, 24];
export const DEFAULT_MONTHS = 12;

// 이탈 위험 이유. 순서가 곧 같은 묶음 안의 급한 순서다(서버 정렬·배지 순서와 같음)
export const RISK_REASONS = [
  { value: "expiring", label: "만료 임박" },
  { value: "grace", label: "재등록 대기" },
  { value: "low_count", label: "횟수 소진 임박" },
  { value: "absent", label: "출석 공백" },
  { value: "churned", label: "최근 이탈" },
];
export const DEFAULT_REASON = "all";

// 이탈 위험 묶음 (members[].tier). 오늘 연락 = 빨간 배지가 있거나 이유가 둘 이상, 나머지는 지켜보기(접어 둠)
export const RISK_TIERS = [
  { value: "urgent", label: "오늘 연락" },
  { value: "watch", label: "지켜보기" },
];

// 연락 상태 거르기. 신규회원 관리와 같은 말이고 최근 7일 안의 연락으로 정한다(contactState)
export const CONTACT_FILTERS = [
  { value: "none", label: CONTACT_STATUSES.none.label },
  { value: "retry", label: CONTACT_STATUSES.retry.label },
  { value: "done", label: CONTACT_STATUSES.done.label },
];
export const DEFAULT_CONTACT = "all";

function firstOf(v) {
  return Array.isArray(v) ? v[0] : v;
}

// 6·12·24만, 그 밖이면 12(서버와 같은 규칙)
export function normalizeMonths(value) {
  const n = Number(firstOf(value));
  return MONTH_OPTIONS.includes(n) ? n : DEFAULT_MONTHS;
}

export function normalizeTab(value) {
  const v = firstOf(value);
  return TABS.some((t) => t.key === v) ? v : DEFAULT_TAB;
}

export function normalizeReason(value) {
  const v = firstOf(value);
  return RISK_REASONS.some((r) => r.value === v) ? v : DEFAULT_REASON;
}

export function normalizeContact(value) {
  const v = firstOf(value);
  return CONTACT_FILTERS.some((c) => c.value === v) ? v : DEFAULT_CONTACT;
}

// URL 쿼리 → 화면 상태. 잘못된 값은 기본값
export function parseTrendsQuery(query = {}) {
  return {
    tab: normalizeTab(query.tab),
    months: normalizeMonths(query.months),
    reason: normalizeReason(query.reason),
    contact: normalizeContact(query.contact),
  };
}

// 화면 상태 → URL 쿼리. 기본값은 주소에 쓰지 않고, reason·contact는 이탈 위험 탭에서만 쓴다
export function buildTrendsQuery({ tab, months, reason, contact } = {}) {
  const query = {};
  const t = normalizeTab(tab);
  const m = normalizeMonths(months);
  const r = normalizeReason(reason);
  const c = normalizeContact(contact);
  if (t !== DEFAULT_TAB) query.tab = t;
  if (m !== DEFAULT_MONTHS) query.months = String(m);
  if (t === "at-risk" && r !== DEFAULT_REASON) query.reason = r;
  if (t === "at-risk" && c !== DEFAULT_CONTACT) query.contact = c;
  return query;
}

// ---------------- API (모두 읽기 전용 GET, 서버가 캐시한다) ----------------

// 503(DB를 지키려고 멈춤)은 전역 오류 팝업 없이 탭 안 문구만 보인다(설계 검토 C12). quietStatuses는 lib/api.js의
// api·apiJson이 읽는 옵션이다(그 상태 코드면 handleApiError를 부르지 않고, fetch에는 넘기지 않는다)
export const TRENDS_REQUEST_OPTIONS = Object.freeze({ quietStatuses: [503] });

// [새로고침]은 refresh=1을 붙인다(사장님 결정 2026-10-16): 서버는 그 탭의 계산이 새로고침 바닥(응답의 refresh_minutes)보다 오래됐으면
// 바로 다시 계산하고, 방금 계산한 값이면 그대로 준다. 그 밖의 요청은 서버 캐시(응답의 recompute_minutes) 안이면 다시 계산하지 않는다
function refreshParam(opts, sep) {
  return opts && opts.refresh ? `${sep}refresh=1` : "";
}

// 월별 추세 (2.1절)
export async function fetchTrendsOverview(months = DEFAULT_MONTHS, opts = {}) {
  return apiJson(
    `/v1/admin/trends/overview?months=${normalizeMonths(months)}${refreshParam(
      opts,
      "&"
    )}`,
    TRENDS_REQUEST_OPTIONS
  );
}

// 이탈 위험 회원 (2.2절, 오늘 기준이라 기간이 없다)
export async function fetchAtRisk(opts = {}) {
  return apiJson(
    `/v1/admin/trends/at-risk${refreshParam(opts, "?")}`,
    TRENDS_REQUEST_OPTIONS
  );
}

// 체험 → 등록 (2.3절)
export async function fetchTrials(months = DEFAULT_MONTHS, opts = {}) {
  return apiJson(
    `/v1/admin/trends/trials?months=${normalizeMonths(months)}${refreshParam(
      opts,
      "&"
    )}`,
    TRENDS_REQUEST_OPTIONS
  );
}

// 운영 지표 (2.4절)
export async function fetchOperations(months = DEFAULT_MONTHS, opts = {}) {
  return apiJson(
    `/v1/admin/trends/operations?months=${normalizeMonths(
      months
    )}${refreshParam(opts, "&")}`,
    TRENDS_REQUEST_OPTIONS
  );
}

// 탭 → 불러오기 함수 (months, { refresh }). 이탈 위험은 months를 쓰지 않는다
export const TAB_FETCHERS = {
  overview: fetchTrendsOverview,
  "at-risk": (months, opts) => fetchAtRisk(opts),
  trials: fetchTrials,
  operations: fetchOperations,
};

export function tabUsesMonths(tab) {
  return tab !== "at-risk";
}

// ---------------- 오류·신선도 문구 ----------------

// 503(TRENDS_001 시간 제한, TRENDS_002 바쁨·작업 시간 예산)은 서버가 DB를 지키려고 멈춘 것이다. 응답의 Retry-After(초)만큼
// [다시 시도]·[새로고침]을 누를 수 없게 하고, 문구는 회원 앱이 정상이라는 말로 시작한다
export const TRENDS_RETRY_DEFAULT_SECONDS = 30;
export const TRENDS_RETRY_MAX_SECONDS = 300;
export const TRENDS_LOAD_ERROR_MESSAGE = "회원권 추세를 불러오지 못했습니다.";

export function isTrendsPaused(error) {
  return error?.response?.status === 503;
}

export function retryAfterSeconds(error) {
  const raw = error?.response?.headers?.get?.("Retry-After");
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n) || n <= 0) return TRENDS_RETRY_DEFAULT_SECONDS;
  return Math.min(n, TRENDS_RETRY_MAX_SECONDS);
}

// secondsLeft: 카운트다운이 남은 초. 0이면 바로 다시 눌러도 된다
export function trendsPausedMessage(secondsLeft) {
  const head = "회원 앱은 정상입니다. 추세 계산만 잠시 멈췄으니";
  return secondsLeft > 0
    ? `${head} ${secondsLeft}초 뒤 다시 눌러 주세요.`
    : `${head} 다시 눌러 주세요.`;
}

export function trendsErrorMessage(error, secondsLeft) {
  if (!isTrendsPaused(error)) return TRENDS_LOAD_ERROR_MESSAGE;
  const left = Number.isFinite(secondsLeft)
    ? secondsLeft
    : retryAfterSeconds(error);
  return trendsPausedMessage(left);
}

// 다른 기간을 읽다 실패해(503 멈춤 포함) 앞서 보던 값을 흐리게 둘 때 그 값이 어느 기간·언제 값인지(리뷰 6차: "6개월"이 켜진 채
// 12개월 값이 그대로 읽혔다). "아래 흐린 값은 앞서 본 12개월 값입니다(14:20 기준)."
export function fallbackNote(data, today) {
  if (!data || !isNum(data.months)) return "";
  const at = asOfLabel(data.generated_at, today);
  return `아래 흐린 값은 앞서 본 ${data.months}개월 값입니다${
    at ? `(${at})` : ""
  }.`;
}

// 서버가 다시 계산하는 간격은 화면에 박아 두지 않고 응답의 공통 칸에서 읽는다(사장님 결정 2026-10-16): recompute_minutes = 서버가
// 다시 계산하는 간격(입력 캐시 TTL, 기본 2시간, TRENDS_CACHE_TTL), refresh_minutes = [새로고침]이 다시 계산하는 바닥(방금 계산한 값이면
// 그대로 주는 시간, 이탈 위험 2분·그 밖 10분), refresh_at = [새로고침]이 다시 계산하는 첫 분. 머리에 늘 보이고, [새로고침]이 같은
// generated_at을 받으면 다음 계산 시각을 알린다(C11)

// 분 → "2시간", "1시간 30분", "30분" (값이 없거나 0 이하면 "")
export function durationLabel(minutes) {
  if (!isNum(minutes) || minutes <= 0) return "";
  const m = Math.round(minutes);
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h === 0) return `${r}분`;
  return r === 0 ? `${h}시간` : `${h}시간 ${r}분`;
}

// 머리의 작은 글: "2시간마다 새로 계산 · [새로고침]은 바로 계산". 응답에 간격이 없으면 ""
// (방금 계산한 값이면 [새로고침]도 그대로라는 것은 그때 뜨는 "이미 최신 계산입니다 · HH:mm 이후 새로 계산"이 말한다)
export function recomputeNote(data) {
  const every = durationLabel(data && data.recompute_minutes);
  return every ? `${every}마다 새로 계산 · [새로고침]은 바로 계산` : "";
}

// [새로고침]이 같은 계산을 받았을 때: 다음 계산 시각 = 응답의 refresh_at("YYYY-MM-DD HH:mm"의 HH:mm, 없으면 시각 없이).
// 서버가 실제 계산 시각(초까지) + 바닥을 분으로 올려 준다: generated_at(초를 버림) + refresh_minutes로 셈하면 최대 59초 일러,
// 그 분이 와도 [새로고침]이 다시 계산하지 않았다
export function alreadyLatestMessage(data) {
  const s = String((data && data.refresh_at) || "");
  const next = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(s) ? s.slice(11, 16) : "";
  return next
    ? `이미 최신 계산입니다 · ${next} 이후 새로 계산`
    : "이미 최신 계산입니다";
}

// 자정을 넘겨 열어 둔 화면: 응답의 today가 브라우저의 오늘(KST)과 다르면 어제 계산한 값이라고 알린다(다시 읽지는 않는다, P5).
// 이탈 위험의 D-n·재등록 대기 날 수와 이번 달 줄이 하루 밀려 있다
export function previousDayNote(data, today) {
  const day = data && data.today ? String(data.today).slice(0, 10) : "";
  if (!day || !today || day === String(today).slice(0, 10)) return "";
  return `${shortDate(
    day
  )}에 계산한 값입니다. 오늘 기준으로 보려면 새로고침을 누르세요.`;
}

// stale: 서버가 DB 보호(시간 제한 뒤 1분 쉼, 작업 시간 예산, 연결 바쁨) 때문에 다시 계산하지 않고 같은 날 앞서 계산한 값을 줌
export function staleNote(data) {
  if (!data || !data.stale) return "";
  const at = noteDateTime(data.generated_at);
  return at
    ? `DB를 지키려고 ${at}에 계산한 값을 보입니다.`
    : "DB를 지키려고 앞서 계산한 값을 보입니다.";
}

// ---------------- 이름·색 지도 ----------------

// 응답 배열의 고정 순서(서버와 같음)
export const MEMBERSHIP_TYPES = [
  { value: "Unlimited", label: "Unlimited" },
  { value: "10회권", label: "10회권" },
  { value: "20회권", label: "20회권" },
  { value: "주2회권", label: "주2회권" },
];
// 횟수권(소진율의 종류별, 서버 countPassTypes). 기간권 Unlimited·주2회권은 뺀다
export const COUNT_PASS_TYPES = MEMBERSHIP_TYPES.filter(
  (t) => t.value === "10회권" || t.value === "20회권"
);

export const CLASS_TYPES = [
  { value: "Hybrid", label: "Hybrid" },
  { value: "Core", label: "Core" },
  { value: "Cardio", label: "Cardio" },
];

// 결제 수단 (회원 상세 회원권 추가의 선택지와 같은 말)
export const PAYMENT_METHODS = [
  { value: "card", label: "카드" },
  { value: "transfer", label: "계좌이체" },
  { value: "cash", label: "현금" },
];

// 매출 나눔 (revenue.by_kind[].kind). 기록 시작 때 이미 다니던 회원의 첫 기록 수강권은 재등록이다
export const REVENUE_KINDS = [
  { value: "new", label: "신규" },
  { value: "renewal", label: "재등록" },
  { value: "return", label: "복귀" },
];

// 재등록·이탈 판정 (decisions의 키). 겹침(overlap)은 응답에만 있고 화면 칸에는 쓰지 않는다(정의 목록에만, 설계 검토 C14)
export const DECISIONS = [
  { value: "renewed", label: "재등록" },
  { value: "churned", label: "이탈" },
  { value: "pending", label: "판정 대기" },
  { value: "early_renewed", label: "미리 재등록" },
];

// 체험 → 등록 단계 (funnel의 키). 누적: 뒤 단계 사람은 앞 단계 수에도 든다
export const FUNNEL_STAGES = [
  { value: "applied", label: "신청" },
  { value: "contacted", label: "연락" },
  { value: "scheduled", label: "체험 예약" },
  { value: "attended", label: "체험 참석" },
  { value: "registered", label: "등록" },
];

// 무료체험 자동 예약 실패 사유 (free_trial_requests.failure_reason)
export const FAILURE_REASONS = [
  { value: "class_not_found", label: "수업 없음" },
  { value: "class_full", label: "정원 마감" },
  { value: "trial_slot_unavailable", label: "체험 자리 없음" },
];

// ISO 요일 1 = 월 … 7 = 일 (operations.slots[].weekday)
export const WEEKDAYS = ["월", "화", "수", "목", "금", "토", "일"];

// 요일 × 시작 시각 표: 기간(최근 3개월 기본)과 기준(예약·출석)
export const SLOT_PERIODS = [
  { value: "recent", label: "최근 3개월" },
  { value: "all", label: "고른 기간 전체" },
];
export const SLOT_MEASURES = [
  { value: "booked", label: "예약 기준" },
  { value: "attended", label: "출석 기준" },
];

function labelOf(list, value) {
  const found = list.find((item) => item.value === value);
  return found ? found.label : value || "";
}

export function membershipTypeLabel(type) {
  return labelOf(MEMBERSHIP_TYPES, type);
}
export function classTypeLabel(type) {
  return labelOf(CLASS_TYPES, type);
}
export function paymentMethodLabel(method) {
  return labelOf(PAYMENT_METHODS, method);
}
export function revenueKindLabel(kind) {
  return labelOf(REVENUE_KINDS, kind);
}
export function decisionLabel(decision) {
  return labelOf(DECISIONS, decision);
}
export function funnelStageLabel(stage) {
  return labelOf(FUNNEL_STAGES, stage);
}
export function failureReasonLabel(reason) {
  return labelOf(FAILURE_REASONS, reason);
}
export function riskReasonLabel(code) {
  return labelOf(RISK_REASONS, code);
}
export function riskTierLabel(tier) {
  return labelOf(RISK_TIERS, tier);
}
export function weekdayLabel(isoWeekday) {
  return WEEKDAYS[Number(isoWeekday) - 1] || "";
}

// 배열 응답(by_type·by_payment·by_kind·failures 등)에서 키로 한 칸 찾기. 없으면 null
export function pick(list, key, value) {
  if (!Array.isArray(list)) return null;
  return list.find((item) => item && item[key] === value) || null;
}

// ---------------- 날짜 ----------------

function ymdToDayNumber(ymd) {
  const s = String(ymd || "").slice(0, 10);
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return NaN;
  return Math.round(Date.UTC(y, m - 1, d) / 86400000);
}

// 두 KST 날짜("YYYY-MM-DD" 또는 "YYYY-MM-DD HH:mm") 사이 날 수(to − from). 잘못된 값이면 NaN
export function daysBetween(from, to) {
  return ymdToDayNumber(to) - ymdToDayNumber(from);
}

// ---------------- 이탈 위험 줄 ----------------

// 배지 색은 서버가 준 level로만 정한다(문턱 7일·14일·0회는 서버 한 곳에, 설계 1.5절). memberNotes.js와 같은 tone 이름
export const REASON_LEVEL_TONES = {
  high: "danger",
  mid: "warning",
  low: "muted",
};

export function reasonTone(reason) {
  return (reason && REASON_LEVEL_TONES[reason.level]) || "";
}

// 다 쓴 횟수권: 끝난 날 e(M)이 만료일보다 이르다(마지막 예약 날에 끝남). 회원 상세의 만료일과 다른 이유를 보이려고 쓴다
export function membershipUsedUp(m) {
  return (
    !!m &&
    m.total_count > 0 &&
    m.remaining_count === 0 &&
    !!m.end_date &&
    !!m.expired_at &&
    String(m.end_date) < String(m.expired_at)
  );
}

// 배지 문구 (reasons[].value의 뜻은 이유마다 다르다, 2.2절). row: 그 줄(수강권이 다 쓴 횟수권이면 문구가 바뀐다)
export function reasonText(reason, row = null) {
  if (!reason) return "";
  const v = Number(reason.value);
  const usedUp = membershipUsedUp(row && row.membership);
  switch (reason.code) {
    case "expiring":
      if (usedUp) return v <= 0 ? "오늘 마지막 예약" : `D-${v} 마지막 예약`;
      return v <= 0 ? "오늘 만료" : `D-${v} 만료 임박`;
    case "grace": {
      // value = 이탈 판정까지 남은 날(0~6), 끝난 지 = 7 − value
      const since = 7 - v;
      const head = usedUp ? `횟수 다 쓰고 ${since}일째` : `종료 ${since}일째`;
      return v <= 0 ? `${head} · 내일 이탈` : `${head} · 이탈까지 ${v}일`;
    }
    case "low_count":
      return `${v}회 남음`;
    case "absent":
      // over: 최근 출석을 찾는 기간(60일, ATTENDANCE_LOOKBACK_DAYS) 안에 출석·휴회 끝·다시 시작이 모두 없음(값 60)
      if (reason.basis === "over") return `${v}일 넘게 출석 없음`;
      if (reason.basis === "hold") return `휴회 끝나고 ${v}일 출석 없음`;
      if (reason.basis === "start") return `시작 후 ${v}일 출석 없음`;
      return `${v}일째 출석 없음`;
    case "churned":
      return usedUp ? `횟수 다 쓰고 ${v}일` : `${v}일 전 종료`;
    default:
      return riskReasonLabel(reason.code);
  }
}

// 수강권 칸: "10회권 4/10회 · ~10.18", 다 쓴 횟수권 "10회권 0/10회 · 다 씀 (만료일 11.30)", 끝난 것 "Unlimited · 끝남 10.07"
export function membershipCellText(m) {
  if (!m) return "";
  const type = membershipTypeLabel(m.type);
  const count = m.total_count === -1 ? "" : ` ${membershipCountText(m)}`;
  if (membershipUsedUp(m)) {
    return `${type}${count} · 다 씀 (만료일 ${shortDate(m.expired_at)})`;
  }
  if (m.state === "ended")
    return `${type}${count} · 끝남 ${shortDate(m.end_date)}`;
  return `${type}${count} · ~${shortDate(m.end_date)}`;
}

// 휴회 중이면 "휴회 중 ~10.20", 아니면 ""
export function holdText(row) {
  const until = row && row.on_hold_until;
  return until ? `휴회 중 ~${shortDate(until)}` : "";
}

// 다음 예약이 있으면 "다음 예약 10.16 19:00", 없으면 ""
export function nextBookingText(row) {
  const at = row && row.next_booking;
  return at ? `다음 예약 ${noteDateTime(at)}` : "";
}

// 줄의 최근 연락 (contact: { at, author, result, content } | null) → "10.14 김코치 · 통화"
export function contactLine(contact) {
  if (!contact) return "";
  const who = [noteDate(contact.at), contact.author].filter(Boolean).join(" ");
  const result = contactResultLabel(contact.result);
  return [who, result].filter(Boolean).join(" · ");
}

// 연락 내용 앞부분(데스크톱 연락 칸 아래 작은 글). 줄바꿈은 공백으로
export const CONTACT_SNIPPET_LENGTH = 30;
export function contactSnippet(contact, max = CONTACT_SNIPPET_LENGTH) {
  const text = String((contact && contact.content) || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return "";
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

// 연락 상태(최근 7일 안의 연락 기준, 이탈 위험 탭): 없음 → none, 마지막이 부재 → retry, 통화·문자 → done
export const CONTACT_FRESH_DAYS = 7;
export function contactState(contact, today) {
  if (!contact) return "none";
  const age = daysBetween(contact.at, today);
  if (!Number.isFinite(age) || age > CONTACT_FRESH_DAYS) return "none";
  return contact.result === "no_answer" ? "retry" : "done";
}

// 이 화면에서 [연락 기록]으로 방금 저장한 줄의 배지. 연락 거르기의 말·색과 맞춘다: 통화·문자는 "방금 연락함"(safe, 연락함),
// 부재는 다시 연락해야 하므로 "방금 기록 · 부재"(warning, 부재 · 다시 필요). 리뷰 2차: 부재도 초록 "방금 연락함"이라 끝난 회원으로 읽혔다
export function justSavedBadge(contact) {
  const result = contact && contact.result;
  if (result === "no_answer")
    return { text: "방금 기록 · 부재", tone: "warning" };
  if (result === "call" || result === "sms") {
    return { text: "방금 연락함", tone: "safe" };
  }
  return { text: "방금 기록함", tone: "info" };
}

// 30일보다 오래된 연락은 회색 "5개월 전"을 함께 보인다. 30일 이하면 ""
export const CONTACT_OLD_DAYS = 30;
export function contactAgeText(contact, today) {
  if (!contact) return "";
  const age = daysBetween(contact.at, today);
  if (!Number.isFinite(age) || age <= CONTACT_OLD_DAYS) return "";
  return `${Math.max(1, Math.floor(age / 30))}개월 전`;
}

// 최근 출석을 찾는 기간(서버 trends.AttendanceLookbackDays와 같음). 이보다 먼저 끝난 출석은 서버가 읽지 않는다(DB 감사: 출석 표시가
// 없는 회원은 예약 이력 전부를 읽었다). 그래서 최근 출석 "없음"은 "최근 60일 안에 없음"이다
export const ATTENDANCE_LOOKBACK_DAYS = 60;

// 최근 출석 칸: 올해면 "10.20", 다른 해면 "25.10.20"(리뷰 6차: 연도 없이 쓰면 지난해 출석이 올해·앞날 날짜처럼 읽혔다. 최근 60일만
// 찾으므로 지금은 1~2월에 지난해 11·12월 출석이 그렇다. 시작·휴회 기준 출석 공백 줄에서는 이 칸만이 마지막으로 온 때를 알려 준다)
export function attendanceDateText(ymd, today) {
  const s = String(ymd || "").slice(0, 10);
  if (!s) return "";
  const year = s.slice(0, 4);
  if (today && year !== String(today).slice(0, 4)) {
    return `${year.slice(2)}.${shortDate(s)}`;
  }
  return shortDate(s);
}

// [연락 기록] 모달이 돌려준 기록(POST 응답) → 줄의 contact 모양. 다시 읽지 않고 그 줄만 바꾼다(R20)
export function contactFromNote(note) {
  if (!note) return null;
  return {
    at: note.created_at || "",
    author: note.author_name || "",
    result: note.contact_result || "",
    content: note.content || "",
  };
}

// 이유·연락 상태로 거르기(화면 안에서만, 서버를 다시 부르지 않음)
export function filterAtRiskRows(rows, { reason, contact, today } = {}) {
  const r = normalizeReason(reason);
  const c = normalizeContact(contact);
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    if (r !== DEFAULT_REASON) {
      const has = (row.reasons || []).some((x) => x.code === r);
      if (!has) return false;
    }
    if (c !== DEFAULT_CONTACT && contactState(row.contact, today) !== c) {
      return false;
    }
    return true;
  });
}

// 묶음 안에서 최근 7일 안에 연락이 된(통화·문자) 줄을 아래로 내린다(흐리게 보임). 방금 모달로 저장한 줄(keep)은
// 저장하기 전에 있던 쪽에 그대로 둔다(화면이 튀지 않게, "방금" 배지): keep은 user_id → "head"(위쪽) | "tail"(아래 연락함 쪽)인 Map,
// 또는 모두 "head"인 Set. 리뷰 4차: 아래쪽(이미 연락함)에서 다시 저장한 줄이 늘 위쪽으로 올라가 화면이 1,800px 움직였다.
// 나머지 순서는 서버 정렬 그대로
export function orderAtRiskRows(rows, today, keep = new Map()) {
  const head = [];
  const tail = [];
  for (const row of Array.isArray(rows) ? rows : []) {
    const kept = keptPart(keep, row.user_id);
    const toTail = kept
      ? kept === "tail"
      : contactState(row.contact, today) === "done";
    if (toTail) tail.push(row);
    else head.push(row);
  }
  return head.concat(tail);
}

function keptPart(keep, id) {
  if (keep instanceof Map)
    return keep.get(id) === "tail" ? "tail" : keep.has(id) ? "head" : "";
  if (keep instanceof Set) return keep.has(id) ? "head" : "";
  return "";
}

// 서버 tier로 두 묶음 나누기
export function rowsByTier(rows) {
  const out = { urgent: [], watch: [] };
  for (const row of Array.isArray(rows) ? rows : []) {
    (row.tier === "urgent" ? out.urgent : out.watch).push(row);
  }
  return out;
}

// ---------------- 형식 ----------------

function isNum(x) {
  return typeof x === "number" && Number.isFinite(x);
}

// 1234000 → "1,234,000원"
export function won(n) {
  if (!isNum(n)) return "—";
  return `${Math.round(n).toLocaleString("ko-KR")}원`;
}

// 타일용 짧은 금액: 1234000 → "123.4만원", 12340000 → "1,234만원", 9000 → "9,000원"
export function manwon(n) {
  if (!isNum(n)) return "—";
  if (Math.abs(n) < 10000) return won(n);
  const man = n / 10000;
  const digits = Math.abs(man) < 1000 ? 1 : 0;
  const text = man.toLocaleString("ko-KR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
  return `${text}만원`;
}

// 비율(0~1) → "21.1%", null이면 "—"
export function pct(x) {
  if (!isNum(x)) return "—";
  return `${(x * 100).toFixed(1)}%`;
}

// 수 → "1,234", null이면 "—"
export function count(n) {
  if (!isNum(n)) return "—";
  return n.toLocaleString("ko-KR");
}

// 소수 한 자리 (1인 주간 출석·평균 일수·평균 남김), null이면 "—"
export function num1(x) {
  if (!isNum(x)) return "—";
  return x.toFixed(1);
}

// 증감: 5 → "+5", -3 → "-3", 0 → "0", null이면 ""
export function deltaText(n) {
  if (!isNum(n)) return "";
  return n > 0 ? `+${count(n)}` : count(n);
}

// 분모가 이보다 작으면 비율을 회색으로, "표본 적음"을 붙이고, 차트 점을 속 빈 원으로 그린다(설계 검토 C6)
export const SMALL_SAMPLE_MIN = 5;
export const SMALL_SAMPLE_LABEL = "표본 적음";
export function isSmallSample(den) {
  return isNum(den) && den < SMALL_SAMPLE_MIN;
}

// 비율과 분자·분모를 함께: (8, 38) → "21.1% (8/38)", 분모 0이면 "—"
export function ratioText(num, den) {
  if (!isNum(num) || !isNum(den) || den <= 0) return "—";
  return `${pct(num / den)} (${count(num)}/${count(den)})`;
}

// "2026-10" → "26.10"
export function monthLabel(ym) {
  const [y, m] = String(ym || "").split("-");
  if (!y || !m) return String(ym || "");
  return `${y.slice(-2)}.${m}`;
}

// "2026-10" → "2026년 10월"
export function monthLabelLong(ym) {
  const [y, m] = String(ym || "").split("-");
  if (!y || !m) return String(ym || "");
  return `${y}년 ${Number(m)}월`;
}

// "2026-10" → "10월" (타일의 "(9월 확정)" 같은 짧은 달)
export function monthLabelShort(ym) {
  const [, m] = String(ym || "").split("-");
  return m ? `${Number(m)}월` : "";
}

// 브라우저의 오늘(KST) "YYYY-MM-DD". 브라우저 시간대와 상관없이 UTC+9로 센다(서버 util.NowInSeoul과 같은 날짜)
export function kstToday(nowMs = Date.now()) {
  return new Date(nowMs + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

// generated_at "2026-10-15 14:20" → "14:20 기준", today(브라우저의 오늘 KST, kstToday)가 아니면 "10.14 23:58 기준"
export function asOfLabel(generatedAt, today) {
  const s = String(generatedAt || "");
  if (!s) return "";
  const sameDay = today && s.slice(0, 10) === String(today).slice(0, 10);
  const text = sameDay ? s.slice(11, 16) : noteDateTime(s);
  return text ? `${text} 기준` : "";
}

// 차트 값 형식 (TrendChart unit)
export function formatUnit(value, unit) {
  switch (unit) {
    case "pct":
      return pct(value);
    case "won":
      return won(value);
    case "manwon":
      return manwon(value);
    default:
      return count(value);
  }
}

// 상태 글자 (표 칸·배지)
export const IN_PROGRESS_LABEL = "진행 중";
export const OPEN_LABEL = "집계 중";
export const NO_DATA_LABEL = "기록 전";

// ---------------- 체험 전환·운영 계산 도우미 ----------------

// 깔때기 단계마다 신청 대비 %와 앞 단계 대비 % (설계 검토 C10)
export function funnelSteps(funnel) {
  const f = funnel || {};
  const applied = f.applied;
  return FUNNEL_STAGES.map((stage, i) => {
    const n = f[stage.value];
    const prev = i === 0 ? null : f[FUNNEL_STAGES[i - 1].value];
    return {
      stage: stage.value,
      label: stage.label,
      count: isNum(n) ? n : null,
      ofApplied: isNum(n) && isNum(applied) && applied > 0 ? n / applied : null,
      ofPrev: isNum(n) && isNum(prev) && prev > 0 ? n / prev : null,
    };
  });
}

// 요일 × 시각 칸의 비율: 예약 기준이면 rate, 출석 기준이면 attended_rate
export function slotRate(slot, measure = "booked") {
  if (!slot) return null;
  const v = measure === "attended" ? slot.attended_rate : slot.rate;
  return isNum(v) ? v : null;
}

// 가장 빈 수업: 지금 시간표에 있는(in_schedule) 칸 중 수업이 minClasses개 이상인 것을 비율 낮은 순으로 n개
export function emptiestSlots(
  slots,
  { n = 5, minClasses = 4, measure = "booked" } = {}
) {
  return (Array.isArray(slots) ? slots : [])
    .filter(
      (s) =>
        s &&
        s.in_schedule !== false &&
        s.classes >= minClasses &&
        isNum(slotRate(s, measure))
    )
    .sort(
      (a, b) =>
        slotRate(a, measure) - slotRate(b, measure) ||
        a.weekday - b.weekday ||
        String(a.start_time).localeCompare(String(b.start_time))
    )
    .slice(0, n);
}

// ---------------- 차트 색 (다크 바탕 #151718에서 검사함, 4.8절) ----------------

// 계열 순서 고정: 1 파랑 → 2 주황 → 3 청록. 같은 실체는 늘 같은 색(매출 나눔·수업 종류·수강권 종류 각각 1·2·3번)
export const SERIES_COLORS = ["#3987e5", "#d95926", "#199e70"];

// 요일 × 시작 시각 채움률 표 바탕(낮음 → 높음). 밝은 세 단은 글자를 검정으로(12px 글자가 4.5:1을 넘게: #3987e5에 흰 글자는
// 3.6:1이라 리뷰 4차에서 검정(5.4:1)으로 바꿨다)
export const HEAT_STEPS = [
  { min: 0, color: "#184f95", text: "#ffffff" },
  { min: 0.4, color: "#256abf", text: "#ffffff" },
  { min: 0.6, color: "#3987e5", text: "#0a0a0a" },
  { min: 0.8, color: "#6da7ec", text: "#0a0a0a" },
  { min: 0.95, color: "#9ec5f4", text: "#0a0a0a" },
];

// 칸 글자와 같은 반올림한 백분율로 단을 고른다(0.796은 "80%"로 보이므로 80~95% 단). 날 값으로 고르면 "80%" 칸이 60~80% 색이었다
export function heatStep(rate) {
  if (!isNum(rate)) return null;
  const shown = Math.round(rate * 100) / 100;
  let step = HEAT_STEPS[0];
  for (const s of HEAT_STEPS) {
    if (shown >= s.min) step = s;
  }
  return step;
}

// ---------------- 보이는 정의 (4.9절과 같은 문구) ----------------

// 짧은 정의(20자 안팎): 타일 아래·이유 필터 아래에 늘 보인다
export const METRIC_SHORT = {
  active: "오늘 쓸 수 있는 수강권이 있는 회원",
  new: "처음 산 수강권이 시작한 회원",
  returned: "7일 넘게 쉬다 다시 산 회원",
  renewal_rate: "끝나고 7일 안에 다음 수강권을 산 비율",
  churn_rate: "끝나고 7일 안에 다음 수강권이 없는 비율",
  revenue: "그 달에 시작한 수강권 금액",
  expiring: "14일 안에 끝나는데 다음 수강권 없음",
  grace: "끝난 지 7일 안, 다음 수강권 없음",
  low_count: "2회 이하 남았는데 다음 수강권 없음",
  absent: "쓰는 중인데 최근 1주 출석 없음",
  churned: "끝난 지 8~30일, 새 수강권 없음",
  registration_rate: "신청한 사람 중 수강권을 산 비율",
  attended_registration_rate: "체험에 온 사람 중 수강권을 산 비율",
  avg_days_to_register: "신청부터 수강권을 만들 때까지",
  per_member_week: "수강권이 있던 날 기준 주당 출석",
  no_show: "출석 체크한 수업에서 오지 않은 비율",
  use_up: "끝난 횟수권 중 다 쓴 비율",
  fill: "끝난 수업의 예약 ÷ 정원",
};

// 긴 정의: 탭 맨 아래 "지표 정의" 칸에 늘 보인다(툴팁 아님)
export const METRIC_DEFINITIONS = {
  // 추세
  active:
    "그 달 마지막 날(이번 달은 오늘) 쓸 수 있는 수강권이 있는 회원 수. 휴회 중도 셉니다.",
  new: "처음 산 수강권이 그 달에 시작한 회원 수. 기록을 시작할 때 이미 다니던 회원(첫 기록 수강권이 기록 시작 뒤 31일 안에 시작)은 신규로 세지 않습니다.",
  returned:
    "수강권이 끝나고 7일 안에 다음 수강권을 사지 않았다가 그 달에 다시 시작한 회원 수.",
  renewal_rate:
    "그 달에 끝난 수강권 중 끝나고 7일 안에 다음 수강권을 사거나 시작한 비율. 끝난 지 8일이 지나 판정이 끝난 수강권만 셉니다.",
  churn_rate:
    "그 달에 끝난 수강권 중 끝나고 7일 안에 다음 수강권을 사지도 시작하지도 않은 비율. 재등록률 + 이탈률 = 100%.",
  pending:
    "끝난 지 7일이 안 됐거나 아직 쓰는 중이라 재등록·이탈을 아직 정할 수 없는 수강권.",
  early_renewed:
    "판정 날(끝나고 8일째)이 오기 전에 이미 다음 수강권을 산 수강권. 같은 때 끝난 수강권의 이탈은 아직 셀 수 없으므로 비율에 넣지 않고 따로 보입니다.",
  overlap:
    "같은 회원이 함께 쓰는 수강권 중 더 늦게 끝나는 것이 있어 판정하지 않는 수강권(한 사람을 두 번 세지 않게).",
  decisions_open:
    "끝난 지 8일이 안 된 수강권이 남은 달은 비율이 아직 바뀝니다(집계 중). 타일은 판정이 다 끝난 가장 최근 달을 보입니다.",
  revenue:
    "그 달에 시작한 수강권 금액 합계(결제일이 아니라 시작일 기준, 취소 제외).",
  revenue_kind:
    "신규 = 첫 수강권, 재등록 = 앞 수강권이 끝나고 7일 안에 산 수강권(기록 시작 때 이미 다니던 회원의 첫 기록 포함), 복귀 = 7일 넘게 쉬고 산 수강권.",
  upcoming:
    "다음 달 이후에 시작하는 수강권(미리 산 것). 매출은 시작하는 달에 듭니다.",
  in_progress: "이번 달은 진행 중이라 숫자가 아직 바뀝니다.",
  no_data: "기록을 시작하기 전 달이라 숫자를 보이지 않습니다.",
  // 기록 전 달의 뜻은 탭마다 다르다(리뷰 3차: 체험 전환·운영도 "숫자를 보이지 않습니다"라고 했는데 숫자가 보였다)
  no_data_trials:
    "수강권 기록을 시작하기 전 달이라 그때의 회원·등록을 다 찾을 수 없습니다. 신청에서 나오는 수(신청·연락·체험·취소·자동 예약 실패)만 보이고 기존 회원이 섞였을 수 있습니다. 기존 회원·등록·등록률·등록까지 칸은 비워 두고(—) 기간 합계에 넣지 않습니다.",
  no_data_operations:
    "수강권 기록을 시작하기 전 달이라 회원 지표(1인 주간 출석·불참률·횟수권 소진율·평균 남김)는 비워 두고(—) 수업 자료인 정원 채움률만 보입니다.",
  small_sample:
    "분모가 5보다 작은 비율과 5개보다 적은 값으로 낸 평균(평균 남김·등록까지 일수)은 회색으로 보입니다(한두 명으로 크게 흔들림).",
  // 이탈 위험
  expiring:
    "사용 중인 수강권이 14일 안에 끝나는데 이어 쓸 수강권이 없음. 횟수권을 다 썼으면 마지막 예약 날이 끝나는 날입니다.",
  grace:
    "마지막 수강권이 끝난 지 1~7일이고 아직 다음 수강권이 없음. 7일이 지나면 이탈이니 다시 등록할 마지막 기회입니다.",
  low_count: "횟수권이 2회 이하 남았는데 다른 수강권이 없음.",
  absent:
    "수강권을 쓰는 중인데 최근 1주(지난주 같은 요일~오늘) 출석이 없음. 그 1주 안에 시작·복귀했거나 휴회 중이었던 회원은 빼고, 날 수는 마지막 출석·휴회 끝·다시 시작한 날 중 가장 늦은 날부터 셉니다. 최근 출석은 최근 60일 안에서만 찾아, 그 안에 셋 다 없으면 '60일 넘게'로 보입니다(최근 출석 칸은 '없음').",
  churned:
    "마지막 수강권이 끝난 지 8~30일 됐고 새 수강권이 없음. 다시 오도록 연락할 회원.",
  urgent:
    "오늘 연락: 빨간 배지(D-6 이하, 재등록 대기, 0회 남음, 출석 공백 14일 이상, 끝난 지 14일 이하)가 있거나 이유가 둘 이상인 회원. 출석 공백 14일 이상이어도 오늘~2일 뒤 예약이 있으면 노란 배지입니다. 나머지는 지켜보기에 접어 둡니다.",
  contact_state:
    "연락 상태는 최근 7일 안의 연락 기록으로 정합니다. 부재는 다시 연락이 필요합니다.",
  reason_count: "한 회원이 여러 이유면 각각 셉니다.",
  // 체험 전환
  applied:
    "그 달에 무료체험 신청을 시작한 사람 수(전화번호로 한 사람, 30일 안에 다시 신청하면 같은 건, 기존 회원·드랍인 제외).",
  contacted:
    "연락완료·예약완료·참석완료 상태이거나 연락한 시각이 남은 사람. 뒤 단계 수(체험 없이 바로 등록한 사람 포함)도 셉니다.",
  scheduled:
    "체험 수업이 잡힌 사람(예약완료·참석완료 상태나 예약된 수업이 있음). 뒤 단계 수(체험 없이 바로 등록한 사람 포함)도 셉니다.",
  attended:
    "신청 상태가 '참석완료'인 사람과 등록한 사람(체험 없이 바로 등록한 사람도 들어가고 그 수는 '바로 등록'으로 따로 보입니다). 참석완료는 등록과 다릅니다. 수업관리에서 출석만 체크하고 상태를 바꾸지 않으면 세지 않습니다.",
  registered:
    "신청 뒤 60일 안에 가격이 있는 수강권을 산 사람. 전화번호가 같은 회원으로 찾습니다(번호가 다르면 세지 못합니다).",
  registration_rate:
    "등록 ÷ 신청(기존 회원 제외). 기간 합계는 신청 뒤 60일이 지나 등록 집계가 끝난 달만 셉니다.",
  attended_registration_rate:
    "체험 참석(참석완료) 뒤 등록한 사람 ÷ 참석완료인 사람(체험 참석 수 − 바로 등록). 체험 없이 바로 등록한 사람은 따로 셉니다.",
  registered_direct: "체험 참석 기록 없이 등록한 사람.",
  existing_members:
    "신청할 때 이미 산 수강권이 신청한 날에 끝나지 않은 사람(쓰는 중이거나 아직 시작 전). 전환율에서 뺍니다. 신청한 날 체험 뒤에 산 수강권은 그날 시작해도 등록으로 셉니다.",
  cancelled: "그 건의 마지막 신청이 예약취소이고 등록하지 않은 사람.",
  failures: "자동 예약이 되지 않은 신청 건수(사유별).",
  avg_days_to_register:
    "신청한 날부터 수강권을 만든 날까지 일수의 평균과 중앙값.",
  registration_open: "신청 뒤 60일이 아직 안 지나 등록 수가 더 늘 수 있습니다.",
  follow_ups:
    "최근 30일 안에 체험에 참석(참석완료)했지만 아직 등록하지 않은 사람.",
  drop_ins:
    "게스트 추가에서 드랍인으로 넣은 방문과, 무료체험에 참석(참석완료)한 번호가 다시 온 방문입니다. 위 체험 전환 수에서는 빠집니다. 사람은 달마다 한 번 세고, 예약취소는 빼고, 수강권이 있는 회원이 게스트로 온 것은 '기존 회원'으로 따로 셉니다.",
  drop_in_registered:
    "그 달 첫 드랍인 뒤 60일 안에 가격이 있는 수강권을 산 사람(체험 등록과 같은 조건). 무료체험 뒤 60일 안이면 체험 전환의 등록에도 함께 듭니다.",
  // 운영
  per_member_week:
    "그 달 출석 수 ÷ 회원들이 수강권을 가진 날 수(휴회 날 뺌) × 7. 이번 달은 오늘까지.",
  no_show:
    "출석 체크를 한 끝난 수업의 회원 예약 중 출석하지 않은 비율. 출석 체크가 하나도 없는 수업은 뺍니다.",
  use_up: "그 달에 끝난 횟수권 중 횟수를 다 쓴 비율과 평균 남긴 횟수.",
  fill: "끝난 수업의 예약 수(무료체험 포함) ÷ 정원. 출석 기준은 실제 출석 ÷ 정원.",
  slots:
    "요일·시작 시각별 정원 채움률. 기본은 최근 3개월(지난 두 달과 이번 달), 고른 기간 전체로 바꿀 수 있습니다.",
  not_in_schedule: "지난달과 이번 달에 수업이 없는 시각(지금 시간표에 없음).",
  overview_table:
    "신규·복귀·이탈은 세는 기준이 달라 활성 증감과 꼭 맞지 않습니다. 재등록률 + 이탈률 = 100%, 판정 대기·미리 재등록은 비율에서 뺍니다.",
};

export function definitionOf(key) {
  return METRIC_DEFINITIONS[key] || "";
}

export function shortDefinitionOf(key) {
  return METRIC_SHORT[key] || METRIC_DEFINITIONS[key] || "";
}
